import {
  Banner,
  BodyText,
  Button,
  Flex,
  FlexItem,
  Heading,
  LoadingAnimation,
  Stack,
} from "@knowtex-ai/components";
import { PauseSolid } from "iconoir-react";
import { useEffect, useRef, type ReactElement } from "react";
import { announce } from "../../../lib/announce";
import css from "../record.module.css";
import AudioTestControl from "./AudioTestControl";
import DeleteAudioModal from "./DeleteAudioModal";
import MicrophonePicker from "./MicrophonePicker";
import RecorderIdleActions from "./RecorderIdleActions";
import RecorderTransport from "./RecorderTransport";
import RecorderUploadActions from "./RecorderUploadActions";
import VolumeMeter from "./VolumeMeter";
import {
  getRecordingStatusAnnouncement,
  shouldShowFloatingIndicator,
  shouldShowIdleMicrophoneTools,
  shouldShowPatientVolumeMeter,
  shouldShowTimer,
  shouldShowVolumeMeters,
  toVolumePercent,
  type MicrophoneOption,
  type RecordingStatus,
} from "./recorderUiRules";

const COLUMN_MAX_WIDTH = 480;

export interface RecorderStageProps {
  isNoteAccordionOpen: boolean;
  showNoAudioWarning: boolean;
  showNoPatientAudioWarning: boolean;
  recordingStatus: RecordingStatus;
  hasRecorded: boolean;
  formattedTime: string;
  permission: boolean;
  onRequestMicrophone: () => void;
  microphoneSrc: string;
  isTelehealth: boolean;
  volumeLevel: number;
  telehealthMicVolumeLevel: number;
  telehealthScreenVolumeLevel: number;
  hasTelehealthScreenAudio: boolean;
  hasDetectedSharedAudio: boolean;
  isAudioTestActive: boolean;
  isAudioTestStarting: boolean;
  onToggleAudioTest: () => void;
  availableMicrophones: MicrophoneOption[];
  selectedMicId: string;
  onMicrophoneChange: (deviceId: string) => void;
  isMergingChunks: boolean;
  isStartingRecording: boolean;
  isAudio: boolean;
  org?: string | null;
  onStartRecording: () => void;
  onReturnHome: () => void;
  canStop: boolean;
  onStopRecording: () => void;
  onTogglePause: () => void;
  onRequestDelete: () => void;
  isLoading: boolean;
  hasFloatingRecordingPopup: boolean;
  isFloatingIndicatorSupported: boolean;
  isFloatingIndicatorOpen: boolean;
  isFloatingIndicatorOpening: boolean;
  onShowFloatingIndicator: () => void;
  readyToUpload: boolean;
  isUploadDisabled: boolean;
  onUpload: () => void;
  showDeleteModal: boolean;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
  showUploadBanner: boolean;
}

export default function RecorderStage({
  isNoteAccordionOpen: _isNoteAccordionOpen,
  showNoAudioWarning,
  showNoPatientAudioWarning,
  recordingStatus,
  hasRecorded,
  formattedTime,
  permission,
  onRequestMicrophone,
  microphoneSrc,
  isTelehealth,
  volumeLevel,
  telehealthMicVolumeLevel,
  telehealthScreenVolumeLevel,
  hasTelehealthScreenAudio,
  hasDetectedSharedAudio,
  isAudioTestActive,
  isAudioTestStarting,
  onToggleAudioTest,
  availableMicrophones,
  selectedMicId,
  onMicrophoneChange,
  isMergingChunks,
  isStartingRecording,
  isAudio,
  org,
  onStartRecording,
  onReturnHome,
  canStop,
  onStopRecording,
  onTogglePause,
  onRequestDelete,
  isLoading,
  hasFloatingRecordingPopup,
  isFloatingIndicatorSupported,
  isFloatingIndicatorOpen,
  isFloatingIndicatorOpening,
  onShowFloatingIndicator,
  readyToUpload,
  isUploadDisabled,
  onUpload,
  showDeleteModal,
  onCancelDelete,
  onConfirmDelete,
  showUploadBanner,
}: RecorderStageProps): ReactElement {
  const isPaused = recordingStatus === "paused";
  const showMeters = shouldShowVolumeMeters({ recordingStatus, isAudioTestActive });
  const showPatientMeter = shouldShowPatientVolumeMeter({
    recordingStatus,
    isAudioTestActive,
    hasTelehealthScreenAudio,
  });
  const showIdleMicTools = shouldShowIdleMicrophoneTools({
    permission,
    recordingStatus,
    hasRecorded,
  });
  const showFloatingIndicator = shouldShowFloatingIndicator({
    recordingStatus,
    hasFloatingRecordingPopup,
    isSupported: isFloatingIndicatorSupported,
    isOpen: isFloatingIndicatorOpen,
    isOpening: isFloatingIndicatorOpening,
  });
  const patientPercent = toVolumePercent(telehealthScreenVolumeLevel);
  const statusAnnouncement = getRecordingStatusAnnouncement({
    recordingStatus,
    hasRecorded,
    isLoading,
    readyToUpload,
  });

  useEffect(() => {
    if (showNoAudioWarning) {
      announce(
        "No audio detected. Knowtex is not able to generate a transcript. Please pause the recording and double check your audio settings.",
        "assertive"
      );
    }
  }, [showNoAudioWarning]);

  useEffect(() => {
    if (showNoPatientAudioWarning) {
      announce(
        "No patient audio detected. Make sure you're sharing the tab with the video visit and that tab audio is shared.",
        "assertive"
      );
    }
  }, [showNoPatientAudioWarning]);

  // Stopping unmounts the transport, and processing finishing unmounts the
  // processing block. Hand focus to whatever comes next so keyboard and screen
  // reader users aren't dropped back at the top of the page.
  const postRecordingRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const region = postRecordingRef.current;
    const active = document.activeElement;
    const focusIsLost = !active || active === document.body || active === region;
    if (!region || !focusIsLost || recordingStatus !== "inactive") {
      return;
    }
    if (!isLoading && !readyToUpload) {
      return;
    }
    const nextAction = region.querySelector<HTMLElement>("button:not([disabled])");
    (nextAction ?? region).focus();
  }, [recordingStatus, isLoading, readyToUpload]);

  return (
    <Flex
      justifyContent="center"
      className="w-full"
      style={{ minHeight: "60vh", paddingInline: 12 }}
    >
      <Stack gap={16} className="w-full" style={{ maxWidth: COLUMN_MAX_WIDTH }}>
        {showNoAudioWarning ? (
          <Banner variant="warning" heading="No audio detected">
            Knowtex is not able to generate a transcript. Please pause the recording and double
            check your audio settings.
          </Banner>
        ) : null}

        {showNoPatientAudioWarning ? (
          <Banner variant="warning" heading="No patient audio detected">
            Make sure you&apos;re sharing the tab with the video visit and that tab audio is shared.
          </Banner>
        ) : null}

        {shouldShowTimer(recordingStatus, hasRecorded) ? (
          <Heading as="p" size="large" className={`${css.timer} text-center`}>
            {formattedTime}
          </Heading>
        ) : null}

        {!permission ? (
          <Button type="button" variant="primary-alt" fullWidth onClick={onRequestMicrophone}>
            Get Microphone
          </Button>
        ) : null}

        {/* Reserves the mic's height so the transport doesn't jump on pause. */}
        <Flex justifyContent="center" alignItems="center" className="h-32 sm:h-40 md:h-48">
          {isPaused ? (
            // Decorative: the "Recording Paused" heading and live region carry the state.
            <PauseSolid
              aria-hidden="true"
              data-testid="paused-indicator"
              className={`${css.pausedIcon} w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24`}
            />
          ) : (
            <img
              src={microphoneSrc}
              alt="Microphone"
              className="w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 max-w-[200px] max-h-[200px]"
            />
          )}
        </Flex>

        {showMeters && !isTelehealth ? (
          <VolumeMeter
            id="voice_activity_detection"
            label="Microphone input level"
            level={volumeLevel}
            paused={isPaused}
            title="Shows how well your microphone is picking up audio"
          />
        ) : null}

        {showMeters && isTelehealth ? (
          <Stack gap={8} className="w-full">
            <VolumeMeter
              label="Provider audio level"
              level={telehealthMicVolumeLevel}
              paused={isPaused}
              caption="Provider Audio"
              title="Shows live microphone level"
            />
            {showPatientMeter ? (
              <VolumeMeter
                label="Patient audio level"
                level={telehealthScreenVolumeLevel}
                variant="patient"
                paused={isPaused}
                caption="Patient Audio"
                valueText={
                  hasDetectedSharedAudio
                    ? `Patient audio detected, ${patientPercent} percent`
                    : "No patient audio detected"
                }
                title="Shows the patient audio that would be included in the recording"
              />
            ) : null}
          </Stack>
        ) : null}

        {availableMicrophones.length > 0 ? (
          <Stack gap={12} className="w-full">
            <Flex alignItems="center" gap={8} className="w-full">
              <FlexItem flex="1 1 0" className="min-w-0">
                <MicrophonePicker
                  microphones={availableMicrophones}
                  selectedMicId={selectedMicId}
                  onChange={onMicrophoneChange}
                />
              </FlexItem>
              {showIdleMicTools ? (
                <AudioTestControl
                  isActive={isAudioTestActive}
                  isStarting={isAudioTestStarting}
                  onToggle={onToggleAudioTest}
                />
              ) : null}
            </Flex>
            {isAudioTestActive ? (
              <div
                className="rounded-lg border border-purple-200 bg-purple-50 px-3 py-2"
                role="status"
                aria-label="Nothing will be recorded until Start Recording is clicked. Test ends automatically after five seconds."
              >
                <BodyText size="small">
                  Nothing will be recorded until
                  <strong className="inline-block rounded bg-purple-200 px-1.5 py-0.5 text-base font-extrabold text-purple-950">
                    Start Recording
                  </strong>
                  is clicked. Test ends automatically after five seconds.
                </BodyText>
              </div>
            ) : null}
          </Stack>
        ) : null}

        <RecorderIdleActions
          permission={permission}
          recordingStatus={recordingStatus}
          hasRecorded={hasRecorded}
          isMergingChunks={isMergingChunks}
          isAudio={isAudio}
          isStartingRecording={isStartingRecording}
          isAudioTestStarting={isAudioTestStarting}
          isAudioTestActive={isAudioTestActive}
          org={org}
          onStartRecording={onStartRecording}
          onReturnHome={onReturnHome}
        />

        <RecorderTransport
          recordingStatus={recordingStatus}
          canStop={canStop}
          onStop={onStopRecording}
          onTogglePause={onTogglePause}
          onDelete={onRequestDelete}
        />

        <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
          {statusAnnouncement}
        </p>

        <div
          ref={postRecordingRef}
          tabIndex={-1}
          className="flex flex-col gap-4 focus:outline-none empty:hidden"
        >
          {isLoading ? (
            <Stack gap={12} className="items-center">
              <Heading as="h2" size="small">
                Processing Audio
              </Heading>
              <LoadingAnimation aria-label="Processing audio" />
            </Stack>
          ) : null}

          {showFloatingIndicator ? (
            <Flex justifyContent="center">
              <Button
                type="button"
                variant="text"
                aria-label="Show floating recording indicator"
                onClick={onShowFloatingIndicator}
              >
                Show floating recording indicator
              </Button>
            </Flex>
          ) : null}

          <RecorderUploadActions
            readyToUpload={readyToUpload}
            isUploadDisabled={isUploadDisabled}
            onUpload={onUpload}
            onDelete={onRequestDelete}
          />
        </div>

        <DeleteAudioModal
          open={showDeleteModal}
          onCancel={onCancelDelete}
          onConfirm={onConfirmDelete}
        />
      </Stack>

      {showUploadBanner ? (
        <>
          <div className="sr-only" role="alert" aria-atomic="true">
            Audio uploaded successfully!
          </div>
          <div
            data-testid="audio-upload-success-banner"
            className={`${css.fixedAfterSidebar} ${css.uploadSuccessBanner}`}
            style={{
              position: "fixed",
              top: 0,
              zIndex: 1000,
            }}
          >
            Audio uploaded successfully!
          </div>
        </>
      ) : null}
    </Flex>
  );
}
